import { Button, Skeleton } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { TABULAR, homeColor, homePastel, homeText } from "../home-theme.ts"
import { HomeCard, type HomeCardEdit } from "./HomeCard.tsx"
import { ChartNoAxesCombinedIcon } from "./home-icons.tsx"

/** One stage of the backend's `lead_stages` breakdown. */
export interface PipelineBucket {
  status: number | string
  label: string
  count: number
  value?: number | string | null
}

/**
 * The established stage order. `lead_stages` already sends all seven, zeros
 * included, in this order; sorting again keeps the bars stable if a backend
 * ever omits one.
 */
const STAGE_ORDER = [
  "New",
  "Contacted",
  "Booked",
  "Exploration",
  "Estimate",
  "Contract",
  "Won"
]

/**
 * Reads the stage buckets out of an adapted `sales_pipeline` chart payload.
 *
 * `null` means the widget is not resolved (loading, or not on the layout);
 * an empty array means it resolved with no leads — two different states, and
 * the card says different things for them.
 */
export const pipelineBucketsFrom = (
  payload: unknown
): PipelineBucket[] | null => {
  const chart = (
    payload as { chart?: { breakdown?: { buckets?: unknown } } } | undefined
  )?.chart
  const buckets = chart?.breakdown?.buckets
  if (!Array.isArray(buckets)) return null
  return buckets
    .filter(
      (b): b is PipelineBucket =>
        b != null &&
        typeof b === "object" &&
        typeof (b as PipelineBucket).label === "string" &&
        typeof (b as PipelineBucket).count === "number"
    )
    .sort((a, b) => {
      const ia = STAGE_ORDER.indexOf(a.label)
      const ib = STAGE_ORDER.indexOf(b.label)
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
    })
}

const BAR_TINTS = [
  homePastel.teal,
  homePastel.sky,
  homePastel.sky,
  homePastel.lilac,
  homePastel.lilac,
  homePastel.peach,
  homePastel.mint
]

interface HomePipelineCardProps {
  /** `undefined` while unresolved; the adapted chart payload once resolved. */
  payload: unknown
  /** Loading until the widgets request answered. */
  loading: boolean
  /** The win-rate KPI, when it is on the layout and resolved. */
  winRatePct: number | null
  /** What the counts are measured as of — stages are a today snapshot. */
  periodLabel: string
  onRetry?: () => void
  error?: boolean
  edit?: HomeCardEdit | null
  /** A user's own name for the widget. */
  title?: string
}

/**
 * §8's Lead pipeline card: every active lead at its lifecycle stage, New →
 * Won, as of today (backend `lead_stages`), with the win rate beside it. Drawn by hand rather than through the
 * shared chart widget because that widget's `breakdown` branch expects
 * profitability buckets (project / revenue / cost), not stages.
 */
export const HomePipelineCard = ({
  payload,
  loading,
  winRatePct,
  periodLabel,
  onRetry,
  error,
  edit,
  title
}: HomePipelineCardProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const buckets = pipelineBucketsFrom(payload)
  const total = buckets?.reduce((sum, b) => sum + b.count, 0) ?? 0
  const max = buckets?.reduce((m, b) => Math.max(m, b.count), 0) ?? 0

  return (
    <HomeCard
      testId="home-pipeline-card"
      edit={edit}
      bodyPadding={0}
      icon={<ChartNoAxesCombinedIcon width={14} height={14} />}
      title={title ?? t("home.pipeline.sales", "Sales pipeline")}
      meta={periodLabel}
      actions={
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => navigate("/leads")}
        >
          {t("common.viewAll", "View all")}
        </Button>
      }
    >
      {error ? (
        <div style={{ padding: "20px 16px", textAlign: "center" }} role="alert">
          <div style={{ ...homeText.body, color: homeColor.inkMuted }}>
            {t("home.pipeline.error", "The pipeline could not be loaded.")}
          </div>
          {onRetry ? (
            <Button type="link" onClick={onRetry}>
              {t("common.retry", "Retry")}
            </Button>
          ) : null}
        </div>
      ) : loading || buckets === null ? (
        <div style={{ padding: 16 }} data-testid="home-pipeline-loading">
          <Skeleton active paragraph={{ rows: 3 }} title={false} />
        </div>
      ) : total === 0 ? (
        <div
          style={{ padding: "24px 16px", textAlign: "center" }}
          data-testid="home-pipeline-empty"
        >
          <span style={{ ...homeText.body, color: homeColor.inkMuted }}>
            {t("home.pipeline.empty", "No active leads in the pipeline.")}
          </span>
        </div>
      ) : (
        <div
          style={{
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 14
          }}
        >
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div>
              <div
                style={{
                  ...homeText.metricValue,
                  ...TABULAR,
                  color: homeColor.teal
                }}
              >
                {total}
              </div>
              <div style={{ ...homeText.label, color: homeColor.inkMuted }}>
                {t("home.pipeline.inPipeline", "Leads in pipeline")}
              </div>
            </div>
            {winRatePct !== null ? (
              <div>
                <div
                  style={{
                    ...homeText.metricValue,
                    ...TABULAR,
                    color: homeColor.ink
                  }}
                >
                  {winRatePct.toFixed(1)}%
                </div>
                <div style={{ ...homeText.label, color: homeColor.inkMuted }}>
                  {t("home.pipeline.winRate", "Win rate")}
                </div>
              </div>
            ) : null}
          </div>
          <div
            className="home-pipeline__bars"
            role="img"
            aria-label={t(
              "home.pipeline.barsLabel",
              "Leads by stage: {{stages}}",
              {
                stages: buckets.map((b) => `${b.label} ${b.count}`).join(", ")
              }
            )}
          >
            {buckets.map((bucket, index) => {
              const tint = BAR_TINTS[index % BAR_TINTS.length]
              const height =
                max > 0 ? Math.max(6, Math.round((bucket.count / max) * 72)) : 6
              return (
                <div key={`${bucket.status}`} className="home-pipeline__bar">
                  <span
                    style={{
                      ...homeText.label,
                      ...TABULAR,
                      color: homeColor.ink
                    }}
                  >
                    {bucket.count}
                  </span>
                  <span
                    aria-hidden
                    style={{
                      height,
                      background: tint.bg,
                      borderTop: `3px solid ${tint.fg}`
                    }}
                    className="home-pipeline__fill"
                  />
                  <span style={{ ...homeText.chip, color: homeColor.inkMuted }}>
                    {bucket.label}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </HomeCard>
  )
}
